import {
  INTEGRATIONS_CONNECTION,
  INTEGRATIONS_CONNECTIONS,
  INTEGRATIONS_CONNECTION_START,
  INTEGRATIONS_EMAILS,
  INTEGRATIONS_EMAIL_DETAIL,
  INTEGRATIONS_EMAILS_SEARCH,
  INTEGRATIONS_FOLDER_COUNTS,
  INTEGRATIONS_EMAIL_ATTACHMENT_DOWNLOAD,
} from '../../constants/endpoints';
import { EndpointContract } from '@ublend-npm/aula-schema';
import request from '../request';

export type IntegrationProvider = 'microsoft' | 'google';

export type IntegrationConnectionStatus = 'active' | 'needs_reauth' | 'revoked';

export type IntegrationConnection = {
  provider: IntegrationProvider;
  status: IntegrationConnectionStatus;
  accountEmail: string | null;
  capabilities: string[];
  scopes: string[];
};

export type EmailRecipient = {
  name?: string;
  address?: string;
};

export type EmailAttachment = {
  id: string;
  name: string;
  contentType: string;
  size: number;
  isInline?: boolean;
  lastModifiedDateTime?: string;
  /** Content-ID matching the `cid:<contentId>` reference in an HTML email body. */
  contentId?: string;
};

type DownloadEmailAttachmentContract = EndpointContract & {
  responseBody: {
    downloadUrl: string;
  };
};

export type EmailItem = {
  id: string;
  conversationId?: string;
  subject: string;
  bodyPreview: string;
  from?: EmailRecipient;
  receivedDateTime?: string;
  hasAttachments?: boolean;
  isRead?: boolean;
  importance?: string;
  webLink?: string;
  replyCount?: number;
};

export const downloadConnectedEmailAttachment = async (
  provider: IntegrationProvider,
  messageId: string,
  attachmentId: string
) => {
  return request<DownloadEmailAttachmentContract>({
    method: 'get',
    url: INTEGRATIONS_EMAIL_ATTACHMENT_DOWNLOAD(
      provider,
      messageId,
      attachmentId
    ),
  });
};

export type EmailDetail = EmailItem & {
  body?: {
    contentType?: string;
    content?: string;
  };
  toRecipients?: EmailRecipient[];
  ccRecipients?: EmailRecipient[];
  bccRecipients?: EmailRecipient[];
  replyTo?: EmailRecipient[];
  sender?: EmailRecipient;
  attachments?: EmailAttachment[];
  thread?: EmailDetail[];
};

export const startConnection = async (
  provider: IntegrationProvider
): Promise<{ authorizeUrl: string }> => {
  const { data } = await request({
    method: 'post',
    url: INTEGRATIONS_CONNECTION_START(provider),
  });
  return data as { authorizeUrl: string };
};

export const getConnections = async (): Promise<{
  connections: IntegrationConnection[];
}> => {
  const { data } = await request({
    method: 'get',
    url: INTEGRATIONS_CONNECTIONS(),
  });
  return data as { connections: IntegrationConnection[] };
};

export const deleteConnection = async (
  provider: IntegrationProvider
): Promise<{ disconnected: true; provider: IntegrationProvider }> => {
  const { data } = await request({
    method: 'delete',
    url: INTEGRATIONS_CONNECTION(provider),
  });
  return data as { disconnected: true; provider: IntegrationProvider };
};

export const getConnectedEmails = async (
  provider: IntegrationProvider,
  options?: { folder?: string; top?: number; nextLink?: string }
): Promise<{ emails: EmailItem[]; nextLink?: string }> => {
  const { data } = await request({
    method: 'get',
    url: INTEGRATIONS_EMAILS(
      provider,
      options?.folder,
      options?.top,
      options?.nextLink
    ),
  });
  return data as { emails: EmailItem[]; nextLink?: string };
};

export const getConnectedEmailDetail = async (
  provider: IntegrationProvider,
  id: string
): Promise<{ email: EmailDetail }> => {
  const { data } = await request({
    method: 'get',
    url: INTEGRATIONS_EMAIL_DETAIL(provider, id),
  });
  return data as { email: EmailDetail };
};

export type MailFolderCounts = {
  folder: string;
  total: number;
  unread: number;
};

export const searchConnectedEmails = async (
  provider: IntegrationProvider,
  query?: string,
  options?: { folder?: string; top?: number; nextLink?: string }
): Promise<{ emails: EmailItem[]; nextLink?: string }> => {
  const { data } = await request({
    method: 'get',
    url: INTEGRATIONS_EMAILS_SEARCH(
      provider,
      query,
      options?.folder,
      options?.top,
      options?.nextLink
    ),
  });
  return data as { emails: EmailItem[]; nextLink?: string };
};

export const getMailFolderCounts = async (
  provider: IntegrationProvider,
  folder: string
): Promise<MailFolderCounts> => {
  const { data } = await request({
    method: 'get',
    url: INTEGRATIONS_FOLDER_COUNTS(provider, folder),
  });
  return data as MailFolderCounts;
};
